import { customAxiosInstanceMutator } from './custom-axios-instance';

const BASE = '/loan/api/v1/settings/individual-pricing/drafts';
export interface PricingPolicy {
  policyId: string; revision: number; effectiveFrom?: string; displayName?: string | null;
  [field: string]: unknown;
}
export interface PricingDraft {
  policy: PricingPolicy;
  validation: { issues?: { field: string; kind: string; message: string }[] };
}
export const getPricingDraft = (id: string) => customAxiosInstanceMutator<PricingDraft>({
  url: `${BASE}/${encodeURIComponent(id)}`, method: 'GET',
});
export const savePricingDraft = (policy: PricingPolicy, reason: string, expectedRevision?: number) =>
  customAxiosInstanceMutator<PricingDraft>({
    url: expectedRevision === undefined ? BASE : `${BASE}/${encodeURIComponent(policy.policyId)}`,
    method: expectedRevision === undefined ? 'POST' : 'PUT',
    data: expectedRevision === undefined ? { policy, reason } : { policy, reason, expectedRevision },
  });
export interface PricingDraftSummary { policyId: string; revision: number; changedAt: string; displayName?: string; }
export interface PricingDraftPage { items?: PricingDraftSummary[]; nextCursor?: string | null; }
export interface PublicationReadiness {
  policyId: string; revision: number; checkedAt: string;
  blockers?: { code: string; field: string; message: string }[];
}

/** Central gateway-backed data only; never substitute browser storage or default policies. */
export const listPricingDrafts = async (after?: string, signal?: AbortSignal) => {
  const page = await customAxiosInstanceMutator<PricingDraftPage>({ url: BASE, method: 'GET',
    params: { limit: 25, ...(after ? { after } : {}) }, signal });
  if (!page || typeof page !== 'object' || Array.isArray(page)
    || (page.items !== undefined && !Array.isArray(page.items))) {
    throw new Error('Invalid pricing draft list response');
  }
  return page;
};

export const getPricingReadiness = (id: string, signal?: AbortSignal) =>
  customAxiosInstanceMutator<PublicationReadiness>({
    url: `${BASE}/${encodeURIComponent(id)}/publication-readiness`, method: 'GET', signal });
export const publishPricingDraft = (id: string, expectedRevision: number, reason: string, displayName?: string) =>
  customAxiosInstanceMutator<{ versionId: string; displayName?: string; effectiveFrom: string; policy: PricingPolicy }>({
    url: `${BASE}/${encodeURIComponent(id)}/publish`, method: 'POST', data: { expectedRevision, reason, displayName },
  });

export function pricingLoadError(error: unknown): string {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 401) return 'Your session has expired. Sign in again.';
  if (status === 403) return 'Your account does not have permission to read individual pricing settings.';
  if (status === 404) return 'The draft or pricing service is not available. Reload the list before retrying.';
  return 'Pricing settings could not be loaded. Please retry. No settings have been changed.';
}

export function pricingActivationError(error: unknown): string {
  const response = (error as { response?: { status?: number; data?: { code?: string } } })?.response;
  const status = response?.status;
  if (response?.data?.code === 'VERSION_NAME_IN_USE') return 'That pricing version name is already in use. Choose another name or generate one automatically.';
  if (response?.data?.code === 'INVALID_VERSION_NAME') return 'Use a pricing version name of at most 80 characters without control characters.';
  if (status === 401) return 'Your session has expired. Sign in again before activating pricing.';
  if (status === 403) return 'Your account does not have permission to activate individual pricing.';
  if (status === 404) return 'The draft or activation service is not available. Reload the list before retrying.';
  if (status === 400) return 'Activation was rejected. Check that the effective time is still in the future (SAST), then save and review the latest revision.';
  if (status === 409) return 'Activation conflicts with the saved revision or published pricing. Reload the list and published pricing before retrying.';
  if (status === 422) return 'Activation requirements were not met. Recheck requirements before retrying.';
  if (status === 503) return 'The server could not store the pricing activation. Reload published pricing to check its status before retrying.';
  return 'Pricing activation could not be confirmed. Reload published pricing to check its status before retrying.';
}

export interface PricingPublication { versionId: string; displayName?: string; policyId: string; revision: number; effectiveFrom: string;
  status: 'ACTIVE' | 'SCHEDULED' | 'REPLACED' | 'CANCELLED'; cancelledAt?: string; rescheduledFrom?: string }
export const listPricingPublications = () => customAxiosInstanceMutator<PricingPublication[]>({
  url: '/loan/api/v1/settings/individual-pricing/publications', method: 'GET',
});

const PUBLICATIONS = '/loan/api/v1/settings/individual-pricing/publications';
export const cancelPricingSchedule = (id: string, reason: string) => customAxiosInstanceMutator<void>({
  url: `${PUBLICATIONS}/${encodeURIComponent(id)}/cancel`, method: 'POST', data: { reason },
});
export const reschedulePricing = (id: string, effectiveFrom: string, reason: string, displayName?: string) =>
  customAxiosInstanceMutator<{ versionId: string; displayName?: string; policy: PricingPolicy }>({
    url: `${PUBLICATIONS}/${encodeURIComponent(id)}/reschedule`, method: 'POST', data: { effectiveFrom, reason, displayName },
  });
export function pricingScheduleError(error: unknown): string {
  const response = (error as { response?: { status?: number; data?: { code?: string } } })?.response;
  if (response?.data?.code === 'VERSION_NAME_IN_USE' || response?.data?.code === 'INVALID_VERSION_NAME') return pricingActivationError(error);
  if (response?.data?.code === 'SCHEDULE_CONFLICT') return 'Another version already starts at that time. Choose a different time or cancel its pending schedule first.';
  if (response?.status === 401) return 'Your session has expired. Sign in again.';
  if (response?.status === 403) return 'Your account does not have permission to change pricing schedules.';
  if (response?.status === 404) return 'This published version could not be found. Reload published pricing.';
  if (response?.status === 409) return 'This version is no longer pending, or its schedule conflicts with another version. Reload published pricing before retrying.';
  if (response?.status === 400) return 'Enter a reason and, for rescheduling, a different effective time that is still in the future (SAST).';
  if (response?.status === 422) return 'This pricing no longer meets the activation requirements. Review a new draft before publishing.';
  return 'The schedule change could not be confirmed. Reload published pricing to check its status before retrying.';
}
